<template>
  <div v-if="!teams || teams.length === 0">Loading or broken</div>
  <div v-else class="table text--primary">
    <thead>
      <th></th>
      <th v-for="week in weeks" :key="week">
        {{ week }}
      </th>
      <th>Total</th>
    </thead>
    <tbody>
      <tr v-for="team in orderedTeams" :key="team.id">
        <td>{{ team.name }}</td>
        <td v-for="week in weeks" :key="week">{{ getWeeklyRecord(team, week) }}</td>
        <td>{{getTotalRecord(team)}}</td>
      </tr>
    </tbody>
  </div>
</template>

<script lang="ts">
import ComparedTeam from "@/shared/teams/team";
import Matchup from "@/shared/matchups/matchup";
import orderBy from 'lodash/orderBy';
import Team from "@/shared/teams/team";
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";

function useComparer() {
  const store = useStore();
  type WeeklyRecord = {
    [index: number]: any;
    wins: number;
    losses: number;
    ties: number;
  };
  type ScoreCounter = {
    [index: number]: any;
    id: number;
    name: string;
    wins: number;
    losses: number;
    ties: number;
    weeklyRecords: WeeklyRecord;
  };
  const teams = ref<Team[]>([]);
  const teamScheduleLookup = ref<ScoreCounter>({} as ScoreCounter);
  const teamAggregateScore = ref<ScoreCounter>({} as ScoreCounter);
  const weeks = ref<number>(0);

  const getWeeklyRecord = function (team: ComparedTeam, week: number) {
    return team.weeklyRecord && team.weeklyRecord[week];
  };

  const getTotalWinPercentage = function (team: Team) {
    const aggregateScores = teamAggregateScore.value[team.id];
    return (
      (aggregateScores.wins + 0.5 * aggregateScores.ties) /
      (aggregateScores.wins + aggregateScores.ties + aggregateScores.losses)
    );
  };

  const getTotalRecord = function (team: Team) {
    const aggregateScores = teamAggregateScore.value[team.id];
    const divisor = teams.value.length / 2;
    if (aggregateScores.ties === 0) {
      return Math.trunc(aggregateScores.wins / divisor) + "-" + Math.trunc(aggregateScores.losses / divisor);
    }

    return Math.trunc(aggregateScores.wins / divisor) + "-" + Math.trunc(aggregateScores.losses / divisor) + "-" + Math.trunc(aggregateScores.ties / divisor);
  };

  function compareSchedules() {
    const season = store.state.league[store.state.league.length - 1];
    teams.value = [];
    if (!season) return;
    weeks.value = season.scoringPeriodId - 1;
    for (let i = 0; i < season.teams.length; i++) {
      teams.value.push(season.teams[i]);
      teamAggregateScore.value[season.teams[i].id] = {
        wins: 0,
        losses: 0,
        ties: 0,
      };
    }

    for (let i = 0; i < teams.value.length; i++) {
      const teamToUse = teams.value[i] as ComparedTeam;

      const scores = season.schedule.map((matchup: Matchup) => {
        if (matchup.away && matchup.away.teamId === teamToUse.id)
          return matchup.away.totalPoints;

        if (matchup.home.teamId === teamToUse.id)
          return matchup.home.totalPoints;
      }).filter((x: number) => x > 0);

      teamToUse.weeklyRecord = [];
      teamScheduleLookup.value[teamToUse.id] = scores;
    }

    for (let teamIndex = 0; teamIndex < teams.value.length; teamIndex++) {
      for (let matchupIndex = 0; matchupIndex < season.schedule.length; matchupIndex++) {
        const team = teams.value[teamIndex] as ComparedTeam;
        const matchup: Matchup = season.schedule[matchupIndex];
        const scoreToCompareAgainst =
          teamScheduleLookup.value[team.id][matchup.matchupPeriodId - 1];

        if (!scoreToCompareAgainst) continue;

        let wins = 0;
        let losses = 0;
        let ties = 0;

        for (let index = 0; index < teams.value.length; index++) {
          if (teams.value[index].id === team.id) continue;

          const opponentScore =
            teamScheduleLookup.value[teams.value[index].id][matchup.matchupPeriodId - 1];
          
          if (!opponentScore) {
            continue;
          } 

          if (scoreToCompareAgainst < opponentScore) {
            losses++;
          } else if (scoreToCompareAgainst > opponentScore) {
            wins++;
          } else {
            ties++;
          }
        }

        teamAggregateScore.value[team.id].name =
          team.location + " " + team.nickname;
        teamAggregateScore.value[team.id].id = team.id;
        teamAggregateScore.value[team.id].wins += wins;
        teamAggregateScore.value[team.id].losses += losses;
        teamAggregateScore.value[team.id].ties += ties;

        if (ties === 0 && team.weeklyRecord) {
          team.weeklyRecord[matchup.matchupPeriodId] = wins + "-" + losses;
        } else if (team.weeklyRecord) {
          team.weeklyRecord[matchup.matchupPeriodId] = wins + "-" + losses + "-" + ties;
        }
      }
    }
  }

  watch(
    () => store.state.leagueId,
    () => {
      compareSchedules();
    }
  );

  watch(
    () => store.state.league,
    () => {
      compareSchedules();
    }
  );

  const orderedTeams = computed(() => {
    return orderBy(teams.value, [(team: Team) => getTotalWinPercentage(team)], ['desc']);
  });

  compareSchedules();

  return {
    getTotalRecord,
    getTotalWinPercentage,
    getWeeklyRecord,
    orderedTeams,
    teamAggregateScore,
    teams,
    weeks,
  };
}

export default {
  setup() {
    return useComparer();
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/variables.scss";
.same-team {
  background-color: $color-secondary-background;
}
</style>